import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCart } from "@/lib/cart";
import { formatBRL } from "@/lib/format";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";

export const Route = createFileRoute("/carrinho")({
  head: () => ({
    meta: [
      { title: "Carrinho — Loja EG Elétrica" },
      {
        name: "description",
        content: "Revise os itens do seu carrinho antes de finalizar a compra.",
      },
      { property: "og:title", content: "Carrinho — Loja EG Elétrica" },
      {
        property: "og:description",
        content: "Revise os itens do seu carrinho antes de finalizar a compra.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const { items, updateQuantity, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  const total = subtotal();

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">Seu Carrinho</h1>

      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-lg bg-white border border-border p-12 text-center">
          <ShoppingBag className="h-16 w-16 text-muted-foreground" />
          <p className="text-muted-foreground">Seu carrinho está vazio</p>
          <Link
            to="/"
            className="rounded-md bg-eg-gold px-6 py-2 font-semibold text-eg-dark hover:bg-eg-goldhover"
          >
            Ver Produtos
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
          <ul className="space-y-3">
            {items.map((item) => (
              <li
                key={item.productId}
                className="flex gap-4 rounded-lg bg-white border border-border p-4"
              >
                {item.image && (
                  <img
                    src={item.image}
                    alt={item.name}
                    loading="lazy"
                    className="h-24 w-24 rounded object-cover flex-shrink-0"
                  />
                )}
                <div className="flex flex-1 flex-col justify-between">
                  <div>
                    <Link
                      to="/produto/$slug"
                      params={{ slug: item.slug }}
                      className="font-medium text-eg-dark hover:text-eg-blue"
                    >
                      {item.name}
                    </Link>
                    <p className="text-sm text-muted-foreground">{formatBRL(item.price)} cada</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 rounded border border-border">
                      <button
                        type="button"
                        className="p-1 hover:bg-muted"
                        onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                        aria-label="Diminuir"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="min-w-[2rem] text-center text-sm">{item.quantity}</span>
                      <button
                        type="button"
                        className="p-1 hover:bg-muted"
                        onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                        aria-label="Aumentar"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <span className="font-semibold text-eg-dark">
                      {formatBRL(item.price * item.quantity)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(item.productId)}
                  aria-label="Remover"
                  className="self-start p-1 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>

          <aside className="rounded-lg bg-white border border-border p-6 h-fit space-y-4">
            <h2 className="font-display font-bold text-eg-dark">Resumo</h2>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-medium">{formatBRL(total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Frete</span>
              <span className="text-muted-foreground">A calcular no checkout</span>
            </div>
            <div className="border-t border-border pt-3 flex justify-between text-base font-bold">
              <span>Total</span>
              <span className="text-eg-goldtext">{formatBRL(total)}</span>
            </div>
            <button
              type="button"
              onClick={() => navigate({ to: "/checkout" })}
              className="w-full rounded-md bg-eg-gold px-4 py-3 font-semibold text-eg-dark hover:bg-eg-goldhover transition-colors"
            >
              Finalizar Compra
            </button>
            <Link to="/" className="block text-center text-sm text-eg-blue hover:underline">
              Continuar comprando
            </Link>
          </aside>
        </div>
      )}
    </div>
  );
}
